<template>
  <div class="graphic-editor-settings">
    <h3 class="tb-label">Fox Foresight Chart</h3>

    <div class="tb-form-group">
      <label class="tb-label">
        Chart Type
      </label>

      <select
        v-model="chartType"
        class="tb-input"
        data-entity="graphics-foxforesight-chartType"
        data-test="graphics-foxforesight-chartType"
        @change="onChartTypeChange"
      >
        <option
          v-for="chart in availableCharts"
          :key="chart.value"
          :value="chart.value"
        >
          {{ chart.label }}
        </option>
      </select>
    </div>

    <div class="tb-form-group">
      <label class="tb-label">
        Date
      </label>

      <input
        v-model="foxDate"
        type="date"
        class="tb-input"
        data-entity="graphics-foxforesight-date"
        data-test="graphics-foxforesight-date"
        @change="onDateChange"
      />
    </div>

    <div class="tb-form-group">
      <label class="tb-label">
        Game
      </label>

      <select
        v-model="foxGame"
        class="tb-input"
        data-entity="graphics-foxforesight-game"
        data-test="graphics-foxforesight-game"
        :disabled="loadingGames"
        @change="onGameChange"
      >
        <option value="">
          {{ loadingGames ? 'Loading games...' : 'Select a game' }}
        </option>
        <option
          v-for="game in availableGames"
          :key="game.id"
          :value="game.id"
        >
          {{ game.name }}
        </option>
      </select>
    </div>

    <!-- Dynamic parameters based on chart type and game -->
    <template v-if="chartParameters.length > 0">
      <div
        v-for="param in chartParameters"
        :key="param.key"
        class="tb-form-group"
      >
        <label class="tb-label">
          {{ param.label }}
          <span v-if="param.required" class="text-danger">*</span>
        </label>

        <!-- Single select (treating multi-select as single for API compatibility) -->
        <select
          v-if="param.type === 'select' || param.type === 'multi-select'"
          :value="getParameterValue(param.key)"
          class="tb-input"
          :data-test="`graphics-foxforesight-${param.key}`"
          :data-entity="`graphics-foxforesight-${param.key}`"
          @input="updateParameter(param.key, $event.target.value)"
        >
          <option value="">
            {{ param.required ? 'Select...' : 'All' }}
          </option>
          <option
            v-for="option in param.options"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </select>

        <!-- Text input -->
        <input
          v-else
          :value="getParameterValue(param.key)"
          type="text"
          class="tb-input"
          :placeholder="param.label"
          :data-test="`graphics-foxforesight-${param.key}`"
          :data-entity="`graphics-foxforesight-${param.key}`"
          @input="updateParameter(param.key, $event.target.value)"
        >
      </div>
    </template>

    <div class="tb-form-group">
      <div class="tb-checkbox">
        <input
          type="checkbox"
          id="transparent"
          v-model="transparent"
          data-entity="graphics-foxforesight-transparent"
          data-test="graphics-foxforesight-transparent"
        />

        <label class="checkbox" for="transparent">Transparent Background</label>
      </div>
    </div>
  </div>
</template>

<script>
import Partners from '../../../../services/partners';

export default {
  name: 'FoxForesightSettings',

  props: {
    element: {
      type: Object,
      required: true,
    },

    graphic: {
      type: Object,
      required: true,
    },

    graphic_fields: {
      type: Object,
      required: true,
    },

    allowUpdate: {
      type: Boolean,
      required: true,
    },
  },

  data() {
    return {
      availableCharts: [],
      availableGames: [],
      loadingGames: false,
      loadingParameters: false,
      chartParameters: [],
    };
  },

  computed: {
    elementFields() {
      return this.graphic_fields?.[this.element.id] || {};
    },

    chartType: {
      get() {
        return this.elementFields?.chartType ?? this.element.chartType ?? '';
      },

      set(value) {
        this.updateField('chartType', value);
        this.updateImageUrl();
      },
    },

    foxDate: {
      get() {
        return this.elementFields?.date ?? this.element.date ?? '';
      },

      set(value) {
        this.updateField('date', value);
        this.updateImageUrl();
      },
    },

    foxGame: {
      get() {
        return this.elementFields?.game ?? this.element.game ?? '';
      },

      set(value) {
        this.updateField('game', value);
        this.updateImageUrl();
      },
    },

    transparent: {
      get() {
        return this.elementFields?.transparent ?? this.element.transparent ?? false;
      },

      set(value) {
        this.updateField('transparent', value);
        this.updateImageUrl();
      },
    },
  },

  async mounted() {
    await this.loadCharts();
    if (this.foxDate) {
      await this.loadGames();
    }

    if (this.chartType && this.foxGame) {
      await this.loadChartParameters();
    }
  },

  methods: {
    updateField(field, value) {
      this.$emit('value', { field, value });
    },

    async loadCharts() {
      try {
        const charts = await Partners.getFoxForesightCharts();
        this.availableCharts = (charts || []).map((chart) => ({
          value: chart.chart_type,
          label: chart.label,
        }));
      } catch (error) {
        console.error('Failed to load charts:', error);
      }
    },

    async loadGames() {
      if (!this.foxDate) return;

      this.loadingGames = true;
      try {
        const games = await Partners.getFoxForesightGames(this.foxDate);
        this.availableGames = (games || []).map((game) => ({
          id: game.game_id,
          name: game.label,
        }));
      } catch (error) {
        console.error('Failed to load games:', error);
      } finally {
        this.loadingGames = false;
      }
    },

    async loadChartParameters() {
      if (!this.chartType || !this.foxGame) return;

      this.loadingParameters = true;
      try {
        const data = await Partners.getFoxForesightChartOptions(this.chartType, this.foxGame);
        this.chartParameters = data.parameters || [];

        // Set defaults for new parameters that don't have values yet
        this.chartParameters.forEach((param) => {
          const existingValue = this.elementFields?.[param.key] ?? this.element[param.key];

          if (!existingValue) {
            if (param.type === 'select' || param.type === 'multi-select') {
              const alwaysEmptyFields = ['player_id', 'hit_result', 'hit_direction', 'pitcher_hand'];
              if (alwaysEmptyFields.includes(param.key)) {
                this.updateField(param.key, '');
              } else {
                const emptyOption = param.options.find((opt) => opt.value === '');
                const defaultValue = emptyOption ? '' : (param.options?.[0]?.value ?? '');
                this.updateField(param.key, defaultValue);
              }
            }
          }
        });
      } catch (error) {
        console.error('Failed to load chart parameters:', error);
        this.chartParameters = [];
      } finally {
        this.loadingParameters = false;
      }
    },

    getParameterValue(key) {
      return this.elementFields?.[key] ?? this.element[key] ?? '';
    },

    updateParameter(key, value) {
      this.updateField(key, value);
      this.updateImageUrl();
    },

    onChartTypeChange() {
      this.availableGames = [];
      this.chartParameters = [];
      this.updateField('game', '');

      if (this.foxDate) {
        this.loadGames();
      }

      this.updateImageUrl();
    },

    onDateChange() {
      this.chartParameters = [];
      this.loadGames();
      this.updateImageUrl();
      this.updateField('game', '');
    },

    onGameChange() {
      this.chartParameters = [];
      if (this.foxGame) {
        this.loadChartParameters();
      }

      this.updateImageUrl();
    },

    updateImageUrl() {
      const { chartType, foxDate, foxGame } = this;

      if (!chartType || !foxDate || !foxGame) {
        this.updateField('imageUrl', '');
        return;
      }

      const params = new URLSearchParams();

      // Add required parameters with API-expected names
      params.append('chart_type', chartType);
      params.append('game_id', foxGame);

      // Add transparent parameter if enabled
      const transparent = this.elementFields?.transparent ?? this.element.transparent;
      if (transparent) {
        params.append('transparent', 'true');
      }

      // Add all other parameters from chart parameters
      this.chartParameters.forEach((param) => {
        const value = this.elementFields?.[param.key] ?? this.element[param.key];
        if (value) {
          params.append(param.key, value);
        }
      });

      const apiUrl = process.env.MONO_API || 'https://api.tagboard.com';
      const url = `${apiUrl}/partners/sportsgold/render?${params.toString()}`;
      this.updateField('imageUrl', url);
    },
  },
};
</script>
